import { formatOrdinal } from './formatOrdinal';

export type BoxDiscountStep = {
  kind: 'box';
  percentage: string;
  fromBox: number;
  toBox: number;
};

export type TimeDiscountStep = {
  kind: 'time';
  percentage: string;
  durationMonths: number;
};

export type DiscountStep = BoxDiscountStep | TimeDiscountStep;

export function formatDiscountMonths(durationMonths: number): string {
  return `${durationMonths} ${durationMonths === 1 ? 'month' : 'months'}`;
}

type StepLabelFormatters = {
  [K in DiscountStep['kind']]: (step: Extract<DiscountStep, { kind: K }>) => string;
};

const STEP_LABEL_FORMATTERS: StepLabelFormatters = {
  box: (step) =>
    step.fromBox === step.toBox
      ? `off the ${formatOrdinal(step.fromBox)} box`
      : `off the ${formatOrdinal(step.fromBox)}-${formatOrdinal(step.toBox)} boxes`,
  time: (step) => `off their first ${formatDiscountMonths(step.durationMonths)}`,
};

const COMPACT_STEP_LABEL_FORMATTERS: StepLabelFormatters = {
  box: (step) =>
    step.fromBox === step.toBox
      ? `${formatOrdinal(step.fromBox)} box`
      : `${formatOrdinal(step.fromBox)}-${formatOrdinal(step.toBox)} box`,
  time: (step) => `first ${formatDiscountMonths(step.durationMonths)}`,
};

export function formatDiscountStepLabel(step: DiscountStep): string {
  return (STEP_LABEL_FORMATTERS[step.kind] as (step: DiscountStep) => string)(step);
}

export function formatCompactDiscountStepLabel(step: DiscountStep): string {
  return (COMPACT_STEP_LABEL_FORMATTERS[step.kind] as (step: DiscountStep) => string)(step);
}

function isBoxStep(step?: DiscountStep): step is BoxDiscountStep {
  return (
    step?.kind === 'box' &&
    typeof step.percentage === 'string' &&
    Number.isInteger(step.fromBox) &&
    Number.isInteger(step.toBox)
  );
}

function isTimeStep(step?: DiscountStep): step is TimeDiscountStep {
  return (
    step?.kind === 'time' &&
    typeof step.percentage === 'string' &&
    Number.isInteger(step.durationMonths)
  );
}

// Gates the three-tier layout: two box steps and a final tier bounded either by box number
// (Bento campaigns) or by time (Core campaigns), carrying the integer ordinals and string
// percentages that layout needs — falls back gracefully on malformed data.
export function hasThreeTierDiscountSteps(
  discountSteps?: DiscountStep[],
): discountSteps is [BoxDiscountStep, BoxDiscountStep, DiscountStep] {
  if (!discountSteps || discountSteps.length !== 3) {
    return false;
  }

  const [firstStep, secondStep, thirdStep] = discountSteps;

  return (
    isBoxStep(firstStep) && isBoxStep(secondStep) && (isBoxStep(thirdStep) || isTimeStep(thirdStep))
  );
}

// In prose the time-bounded tier spells out that it covers every remaining box; the compact
// tier badge (formatDiscountStepLabel) has no room for it.
export function formatDiscountStepSentenceLabel(step: DiscountStep): string {
  return step.kind === 'time'
    ? `off all other boxes in their first ${formatDiscountMonths(step.durationMonths)}`
    : formatDiscountStepLabel(step);
}

export function formatSteppedDiscountCopyFragment(
  discountSteps: [BoxDiscountStep, BoxDiscountStep, DiscountStep],
): string {
  const [firstStep, secondStep, thirdStep] = discountSteps;

  return `${firstStep.percentage} off their first box, ${secondStep.percentage} off their second box and ${thirdStep.percentage} ${formatDiscountStepSentenceLabel(thirdStep)}`;
}
